// ./src/components/features/VolunteerSlots/VolunteerSlotsList/VolunteerSlotsList.tsx
//
// Event volunteer slots — grouped by role on desktop (a Table with per-role heading
// rows) and cards on tablet/mobile. Tap-safe, rights-gated, selection-ready. Grouping
// mirrors the signup/POI grouped-list pattern, keyed on the slot's role.

import React, { useCallback } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Badge,
  ThemeIcon,
  ActionIcon,
  Tooltip,
  Skeleton,
  Table,
  Checkbox,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { STATUSES } from "@sdflc/utils";

import { VolunteerSlot } from "@/utils/api";
import { RowSelection } from "@/hooks";
import { useAppEnvironment } from "@/contexts";
import { useOptionalVolunteerSlotsContext } from "@/contexts/VolunteerSlotsContext";
import { AppIcon, EmptyState, SwipeableCard, SwipeAction, useSwipeableList, LoadMoreButton, CardSkeletonActions, CardSkeletonCheckbox, CardSkeletonLines, countCardActions } from "@/components/generic";
import { formatCurrency, formatEventDateTimeRange, recordHasRight, HIGHLIGHT_STYLE, HIGHLIGHT_ROW_BG } from "@/utils";

// ============================================
// Groups
// ============================================

export interface VolunteerSlotGroup {
  key: string;
  label: string;
  color?: string | null;
  icon?: string | null;
  items: VolunteerSlot[];
}

export interface VolunteerSlotsListProps {
  data?: VolunteerSlot[];
  groups?: VolunteerSlotGroup[];
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onEditSlot?: (slot: VolunteerSlot) => void;
  onDeleteSlot?: (slot: VolunteerSlot) => void;
  onManageCrew?: (slot: VolunteerSlot) => void;
  isRemoving?: (id?: string) => boolean;
  highlightedId?: string | null;
  onAddSlot?: () => void;
  selection?: RowSelection;
  selectable?: boolean;
  hasQueryNarrowing?: boolean;
  /** IANA timezone of the event — shift times are shown in this zone. */
  eventTimezone?: string | null;
  /** #A4.7 — the event's currency, for the pay badge. */
  currencyCode?: string | null;
  /** #A4.7 — the hourly rate of a slot's role (the slot's own rate overrides it); null = unpaid. */
  roleRateFor?: (slot: VolunteerSlot) => number | null;
}

// ============================================
// Helpers
// ============================================

const isDisabled = (slot: VolunteerSlot): boolean => slot.status === STATUSES.DISABLED;

// Times are stored UTC; show them in the EVENT's timezone (so "10:00 AM" reads the same
// to everyone). Falls back to the viewer's zone when the event has no timezone set.
const whenLabel = (slot: VolunteerSlot, eventTimezone?: string | null): string =>
  formatEventDateTimeRange(slot.startsAt, slot.endsAt, eventTimezone) || "—";

const whereLabel = (slot: VolunteerSlot): string => {
  if (slot.locationLabel) {
    return slot.locationLabel;
  }
  if (slot.streetAddress) {
    return slot.streetAddress;
  }
  if (slot.locationKind === "coordinates" && slot.latitude != null && slot.longitude != null) {
    return `${slot.latitude.toFixed(4)}, ${slot.longitude.toFixed(4)}`;
  }
  return "—";
};

const rewardCount = (slot: VolunteerSlot): number => (slot.rewards || []).length;

interface FillMeta {
  needed: number;
  assigned: number;
  filled: boolean;
  /** MORE crew assigned than the shift asks for — allowed, but the host should see it. */
  over: boolean;
  remaining: number;
}

const fillMeta = (slot: VolunteerSlot): FillMeta => {
  const needed = Math.max(1, slot.quantityNeeded ?? 1);
  const assigned = Math.max(0, slot.assignedCount ?? 0);
  return { needed, assigned, filled: assigned >= needed, over: assigned > needed, remaining: Math.max(0, needed - assigned) };
};

// ⚠ OVER-STAFFED IS ITS OWN STATE, not a second kind of "full" (owner ruling, 2026-09-22). The server
// deliberately lets a HOST approve past `quantityNeeded` — hosts over-recruit because volunteer
// no-shows are normal — so "7/5" is a legitimate thing to be looking at, and it must not read as the
// calm green of "5/5, done". Orange says *this is on purpose and worth knowing*; red is reserved for
// nobody signed up yet, which is a problem.
const fillColor = (m: FillMeta): string => (m.over ? "orange" : m.filled ? "teal" : m.assigned > 0 ? "yellow" : "red");

const groupFill = (items: VolunteerSlot[]): { assigned: number; needed: number; allFilled: boolean } => {
  let assigned = 0;
  let needed = 0;
  let complete = 0;

  items.forEach((s) => {
    const m = fillMeta(s);
    assigned += m.assigned;
    needed += m.needed;

    if (m.filled) {
      complete += 1;
    }
  });

  return { assigned, needed, allFilled: items.length > 0 && complete === items.length };
};

// ============================================
// Sub-components
// ============================================

// Public sign-ups on this shift (#A4.6): whether the website takes them (and confirms them automatically), and how
// many requests are still waiting on the host — the row's nudge towards the Applications inbox.
const SignUpBadges: React.FC<{ slot: VolunteerSlot }> = ({ slot }) => {
  const { t } = useTranslation(["volunteers"]);
  const waiting = Number(slot.pendingRequestCount ?? 0);

  if (!slot.isOpenForApplications && waiting === 0) {
    return null;
  }

  return (
    <>
      {slot.isOpenForApplications ? (
        <Tooltip
          label={
            slot.approvalMode === "auto"
              ? t("volunteers:slots.signUps.autoTip", { defaultValue: "Open on the event website — sign-ups are confirmed automatically" })
              : t("volunteers:slots.signUps.reviewTip", { defaultValue: "Open on the event website — you approve each sign-up" })
          }
          withArrow
          position="top"
        >
          <Badge size="sm" variant="light" color="blue" leftSection={<AppIcon iconName="World" size={12} />}>
            {slot.approvalMode === "auto"
              ? t("volunteers:slots.signUps.auto", { defaultValue: "Open · auto" })
              : t("volunteers:slots.signUps.open", { defaultValue: "Open" })}
          </Badge>
        </Tooltip>
      ) : null}
      {waiting > 0 ? (
        <Badge size="sm" variant="light" color="orange">
          {t("volunteers:slots.signUps.waiting", { defaultValue: "{{count}} waiting", count: waiting })}
        </Badge>
      ) : null}
    </>
  );
};

// #A4.7 staff pay — what a shift pays per hour: its own rate, else its role's. Nothing for an unpaid shift.
const PayBadge: React.FC<{ slot: VolunteerSlot; roleRate: number | null; currencyCode?: string | null }> = ({ slot, roleRate, currencyCode }) => {
  const { t } = useTranslation(["volunteers"]);
  const own = slot.hourlyRate ?? null;
  const rate = own ?? roleRate;

  if (rate == null) {
    return null;
  }

  return (
    <Tooltip
      label={
        own != null
          ? t("volunteers:slots.pay.ownTip", { defaultValue: "This shift's own rate — it overrides the role's" })
          : t("volunteers:slots.pay.roleTip", { defaultValue: "The role's rate" })
      }
      withArrow
      position="top"
    >
      <Badge size="sm" variant="light" color="teal" leftSection={<AppIcon iconName="Coin" size={12} />}>
        {t("volunteers:slots.pay.rate", { defaultValue: "{{rate}} / h", rate: formatCurrency(rate, currencyCode || undefined) })}
      </Badge>
    </Tooltip>
  );
};

// Filled-vs-needed indicator: how many crew are assigned against how many the slot needs.
// Orange when OVER-staffed, green when exactly staffed, amber when partially, red when nobody yet.
const FillBadge: React.FC<{ slot: VolunteerSlot; size?: "sm" | "md" }> = ({ slot, size = "md" }) => {
  const { t } = useTranslation(["volunteers"]);
  const m = fillMeta(slot);

  // ⚠ The over-staffed tooltip says the NUMBER over, not just "over" — a host who over-recruited on
  // purpose wants to know by how much, and a host who did it by accident needs the same sentence.
  const tip = m.over
    ? t("volunteers:slots.fill.over", {
        defaultValue: "{{count}} more than needed — allowed, in case of no-shows",
        count: m.assigned - m.needed,
      })
    : m.filled
      ? t("volunteers:slots.fill.full", { defaultValue: "Fully staffed" })
      : t("volunteers:slots.fill.remaining", { defaultValue: "{{count}} more needed", count: m.remaining });

  return (
    <Tooltip label={tip} withArrow position="top">
      <Badge
        size={size}
        variant={m.filled ? "filled" : "light"}
        color={fillColor(m)}
        leftSection={<AppIcon iconName={m.over ? "AlertTriangle" : m.filled ? "Check" : "Users"} size={12} />}
      >
        {m.assigned}/{m.needed}
      </Badge>
    </Tooltip>
  );
};

const GroupFillBadge: React.FC<{ items: VolunteerSlot[] }> = ({ items }) => {
  const { t } = useTranslation(["volunteers"]);
  const { assigned, needed, allFilled } = groupFill(items);

  return (
    <Tooltip label={t("volunteers:slots.fill.groupTip", { defaultValue: "{{assigned}} of {{needed}} spots filled", assigned, needed })} withArrow position="top">
      <Badge size="sm" variant={allFilled ? "filled" : "light"} color={allFilled ? "teal" : "gray"} leftSection={<AppIcon iconName={allFilled ? "Check" : "Users"} size={12} />}>
        {assigned}/{needed}
      </Badge>
    </Tooltip>
  );
};

const StatusBadge: React.FC<{ slot: VolunteerSlot }> = ({ slot }) => {
  const { t } = useTranslation(["common"]);

  if (!isDisabled(slot)) {
    return null;
  }

  return (
    <Badge size="xs" variant="light" color="gray">
      {t("common:status.disabled", { defaultValue: "Disabled" })}
    </Badge>
  );
};

const RowActions: React.FC<{
  slot: VolunteerSlot;
  onEdit?: (s: VolunteerSlot) => void;
  onDelete?: (s: VolunteerSlot) => void;
  onManageCrew?: (s: VolunteerSlot) => void;
  isDeleting: boolean;
}> = ({ slot, onEdit, onDelete, onManageCrew, isDeleting }) => {
  const { t } = useTranslation(["volunteers", "common"]);

  const showEdit = !!onEdit && recordHasRight(slot, "edit");
  const showDelete = !!onDelete && recordHasRight(slot, "remove");
  const showCrew = !!onManageCrew && recordHasRight(slot, "edit");

  if (!showEdit && !showDelete && !showCrew) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      {showCrew && onManageCrew && (
        <Tooltip label={t("volunteers:slots.actions.crew", { defaultValue: "Assign crew" })} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="grape"
            size="md"
            onClick={() => onManageCrew(slot)}
            disabled={isDeleting}
            aria-label={t("volunteers:slots.actions.crew", { defaultValue: "Assign crew" })}
          >
            <AppIcon iconName="Users" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showEdit && onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon variant="subtle" color="blue" size="md" onClick={() => onEdit(slot)} disabled={isDeleting} aria-label={t("common:buttons.edit")}>
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon variant="subtle" color="red" size="md" onClick={() => onDelete(slot)} loading={isDeleting} disabled={isDeleting} aria-label={t("common:buttons.delete")}>
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

interface RowProps {
  slot: VolunteerSlot;
  name: string;
  disabled: boolean;
  eventTimezone?: string | null;
  currencyCode?: string | null;
  roleRate: number | null;
  onEdit?: (s: VolunteerSlot) => void;
  onDelete?: (s: VolunteerSlot) => void;
  onManageCrew?: (s: VolunteerSlot) => void;
  highlighted: boolean;
  isDeleting: boolean;
  selectionColumn: boolean;
  selectable: boolean;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect?: (id: string) => void;
}

const SlotTableRow: React.FC<RowProps> = ({
  slot,
  name,
  disabled,
  eventTimezone,
  currencyCode,
  roleRate,
  onEdit,
  onDelete,
  onManageCrew,
  highlighted,
  isDeleting,
  selectionColumn,
  selectable,
  selected,
  onToggleSelect,
}) => {
  const { t } = useTranslation(["volunteers"]);

  return (
    <Table.Tr
      style={{
        opacity: isDeleting ? 0.5 : disabled ? 0.6 : 1,
        backgroundColor: highlighted ? HIGHLIGHT_ROW_BG : selected ? "var(--mantine-color-blue-light)" : undefined,
        transition: "background-color 150ms ease, opacity 150ms ease",
      }}
    >
      {selectionColumn && (
        <Table.Td style={{ width: 44 }}>
          {selectable && <Checkbox checked={selected} onChange={() => slot.id && onToggleSelect?.(slot.id)} disabled={isDeleting} aria-label={name} />}
        </Table.Td>
      )}
      <Table.Td>
        <Group gap={6} wrap="nowrap">
          <Text size="md" fw={600} lineClamp={1}>
            {name}
          </Text>
          <StatusBadge slot={slot} />
          <PayBadge slot={slot} roleRate={roleRate} currencyCode={currencyCode} />
          <SignUpBadges slot={slot} />
        </Group>
      </Table.Td>
      <Table.Td style={{ width: 110 }}>
        <FillBadge slot={slot} />
      </Table.Td>
      <Table.Td style={{ width: 240 }}>
        <Text size="sm" c="dimmed" lineClamp={1}>
          {whenLabel(slot, eventTimezone)}
        </Text>
      </Table.Td>
      <Table.Td style={{ width: 220 }}>
        <Text size="sm" c="dimmed" lineClamp={1}>
          {whereLabel(slot)}
        </Text>
      </Table.Td>
      <Table.Td style={{ width: 120 }}>
        {rewardCount(slot) > 0 ? (
          <Badge size="sm" variant="outline" color="teal" leftSection={<AppIcon iconName="RosetteDiscount" size={10} />}>
            {t("volunteers:slots.rewardCount", { defaultValue: "{{count}} voucher", count: rewardCount(slot) })}
          </Badge>
        ) : (
          <Text size="xs" c="dimmed">
            —
          </Text>
        )}
      </Table.Td>
      <Table.Td style={{ width: 140 }}>
        <RowActions slot={slot} onEdit={onEdit} onDelete={onDelete} onManageCrew={onManageCrew} isDeleting={isDeleting} />
      </Table.Td>
    </Table.Tr>
  );
};

const SlotTableRowSkeleton: React.FC<{ selectionColumn?: boolean }> = ({ selectionColumn }) => (
  <Table.Tr>
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        <Skeleton height={20} width={20} radius="sm" />
      </Table.Td>
    )}
    <Table.Td>
      <Skeleton height={14} width="40%" maw={200} />
    </Table.Td>
    <Table.Td style={{ width: 110 }}>
      <Skeleton height={18} width={56} radius="xl" />
    </Table.Td>
    <Table.Td style={{ width: 240 }}>
      <Skeleton height={14} width={160} />
    </Table.Td>
    <Table.Td style={{ width: 220 }}>
      <Skeleton height={14} width={140} />
    </Table.Td>
    <Table.Td style={{ width: 120 }}>
      <Skeleton height={18} width={70} radius="xl" />
    </Table.Td>
    <Table.Td style={{ width: 140 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const SlotCardComponent: React.FC<CardProps> = ({
  slot,
  name,
  disabled,
  eventTimezone,
  currencyCode,
  roleRate,
  onEdit,
  onDelete,
  onManageCrew,
  highlighted,
  isDeleting,
  selectable,
  selected,
  selectionMode,
  onToggleSelect,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["volunteers", "common"]);

  const toggleSelect = () => slot.id && onToggleSelect?.(slot.id);

  const swipeActions = React.useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (onManageCrew && recordHasRight(slot, "edit")) {
      actions.push({ key: "crew", icon: "Users", label: t("volunteers:slots.actions.crew", { defaultValue: "Assign crew" }), color: "grape", onClick: () => onManageCrew(slot), disabled: isDeleting, secondary: true });
    }
    if (onEdit && recordHasRight(slot, "edit")) {
      actions.push({ key: "edit", icon: "Edit", label: t("common:buttons.edit"), color: "blue", onClick: () => onEdit(slot), disabled: isDeleting });
    }
    if (onDelete && recordHasRight(slot, "remove")) {
      actions.push({ key: "delete", icon: "Trash", label: t("common:buttons.delete"), color: "red", onClick: () => onDelete(slot), loading: isDeleting, disabled: isDeleting });
    }

    return actions;
  }, [onManageCrew, onEdit, onDelete, slot, isDeleting, t]);

  const cardBody = (showActions: boolean, showCheckbox: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : disabled ? 0.6 : 1}
      style={{ transition: "opacity 150ms ease, box-shadow 150ms ease", backgroundColor: selected ? "var(--mantine-color-blue-light)" : undefined, ...(highlighted ? HIGHLIGHT_STYLE : {}) }}
    >
      <Group gap="md" wrap="nowrap" align="flex-start">
        {showCheckbox && <Checkbox checked={selected} onChange={toggleSelect} onClick={(e) => e.stopPropagation()} disabled={isDeleting} aria-label={name} mt={4} />}
        <ThemeIcon variant="light" color="grape" radius="xl" size={40}>
          <AppIcon iconName="Users" size={22} />
        </ThemeIcon>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="md" fw={600} lineClamp={1}>
              {name}
            </Text>
            <FillBadge slot={slot} size="sm" />
            <StatusBadge slot={slot} />
            <PayBadge slot={slot} roleRate={roleRate} currencyCode={currencyCode} />
            <SignUpBadges slot={slot} />
          </Group>
          <Text size="sm" c="dimmed" lineClamp={1} mt={2}>
            {whenLabel(slot, eventTimezone)}
          </Text>
          <Text size="sm" c="dimmed" lineClamp={1}>
            {whereLabel(slot)}
          </Text>
          {rewardCount(slot) > 0 && (
            <Badge size="sm" variant="outline" color="teal" mt={6} leftSection={<AppIcon iconName="RosetteDiscount" size={10} />}>
              {t("volunteers:slots.rewardCount", { defaultValue: "{{count}} voucher", count: rewardCount(slot) })}
            </Badge>
          )}
        </Box>
        {showActions && <RowActions slot={slot} onEdit={onEdit} onDelete={onDelete} onManageCrew={onManageCrew} isDeleting={isDeleting} />}
      </Group>
    </Paper>
  );

  if (selectionMode) {
    return (
      <>
        <Box hiddenFrom="sm" onClick={selectable ? toggleSelect : undefined} style={{ cursor: selectable ? "pointer" : "default", userSelect: "none" }}>
          {cardBody(false, selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
      </>
    );
  }

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard leftSwipeActions={swipeActions} forceClose={forceClose} onForceCloseComplete={onForceCloseComplete} onSwipeStateChange={onSwipeStateChange} disabled={isDeleting} borderRadius="var(--mantine-radius-md)">
            {cardBody(false, false)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
    </>
  );
};

const SlotCard = React.memo(SlotCardComponent);

// ============================================
// Main
// ============================================

const VolunteerSlotsListComponent: React.FC<VolunteerSlotsListProps> = ({
  data: dataProp,
  groups,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditSlot,
  onDeleteSlot,
  onManageCrew,
  isRemoving: isRemovingProp,
  highlightedId,
  onAddSlot,
  selection,
  selectable = false,
  hasQueryNarrowing = false,
  eventTimezone,
  currencyCode,
  roleRateFor,
}) => {
  const { t } = useTranslation(["volunteers", "common"]);
  const { isDesktop } = useAppEnvironment();

  const isSelectable = selectable && !!selection;
  // The tablet card's row buttons, one per handler — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onManageCrew, onEditSlot, onDeleteSlot);

  const slotsContext = useOptionalVolunteerSlotsContext();

  const slots = groups ? groups.flatMap((g) => g.items) : dataProp ?? slotsContext?.volunteerSlots ?? [];
  const loading = loadingProp ?? slotsContext?.loading ?? false;
  const loadingMore = loadingMoreProp ?? slotsContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? slotsContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? slotsContext?.loadMore;
  const isRemoving = isRemovingProp ?? slotsContext?.isRemoving ?? (() => false);

  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();

  const handleEdit = useCallback(
    (slot: VolunteerSlot) => {
      closeAllCards();
      onEditSlot?.(slot);
    },
    [onEditSlot, closeAllCards]
  );

  const handleDelete = useCallback(
    (slot: VolunteerSlot) => {
      closeAllCards();
      onDeleteSlot?.(slot);
    },
    [onDeleteSlot, closeAllCards]
  );

  const handleCrew = useCallback(
    (slot: VolunteerSlot) => {
      closeAllCards();
      onManageCrew?.(slot);
    },
    [onManageCrew, closeAllCards]
  );

  const handleSwipeStateChange = useCallback((id: string, isOpen: boolean) => {
    if (isOpen) {
      openCard(id);
    }
  }, [openCard]);

  // ⚠ A SKELETON IS FOR THE FIRST READ ONLY. `loading && x.length === 0` re-shows it on every
  // refetch of an EMPTY list, so an empty page reads skeleton → "none" → skeleton → "none". Once
  // the first read is back, a refetch keeps the rows OR the empty state and ContentLoadingOverlay
  // says it is reloading. ⚠ The fallback answers "is this all the data there will ever be?" for a
  // caller with no provider. ▶ npm run check-list-states
  const isLoaded = slotsContext?.isLoaded ?? (groups !== undefined || dataProp !== undefined);
  const showSkeleton = !isLoaded;

  if (!showSkeleton && slots.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Paper withBorder p="xl" radius="md">
          <EmptyState title={t("volunteers:slots.emptyState.noMatchTitle", { defaultValue: "No slots match" })} description={t("volunteers:slots.emptyState.noMatchDescription", { defaultValue: "Try a different search or clear the filters." })} iconName="Search" size="md" />
        </Paper>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState title={t("volunteers:slots.emptyState.title", { defaultValue: "No volunteer slots yet" })} description={t("volunteers:slots.emptyState.description", { defaultValue: "Add the roles you need staffed — quantity, time and location." })} iconName="Users" actionLabel={t("volunteers:slots.emptyState.actionLabel", { defaultValue: "Add slot" })} onAction={onAddSlot} size="md" />
      </Paper>
    );
  }

  const rowIsSelectable = (slot: VolunteerSlot): boolean => isSelectable && ((!!onEditSlot && recordHasRight(slot, "edit")) || (!!onDeleteSlot && recordHasRight(slot, "remove")));

  const rowProps = (slot: VolunteerSlot): RowProps => ({
    slot,
    name: slot.name || "—",
    disabled: isDisabled(slot),
    eventTimezone,
    currencyCode,
    roleRate: roleRateFor ? roleRateFor(slot) : null,
    onEdit: onEditSlot ? handleEdit : undefined,
    onDelete: onDeleteSlot ? handleDelete : undefined,
    onManageCrew: onManageCrew ? handleCrew : undefined,
    highlighted: slot.id === highlightedId,
    isDeleting: isRemoving(slot.id),
    selectionColumn: isSelectable,
    selectable: rowIsSelectable(slot),
    selected: selection?.isSelected(slot.id) ?? false,
    selectionMode: selection?.selectionMode ?? false,
    onToggleSelect: selection?.toggle,
  });

  const selectableVisibleIds = slots.filter((s) => rowIsSelectable(s)).map((s) => s.id).filter((id): id is string => !!id);

  const colSpan = (isSelectable ? 1 : 0) + 6;

  const loadMore = <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />;

  const groupHeadingRow = (g: VolunteerSlotGroup) => (
    <Table.Tr key={`h-${g.key}`} style={{ backgroundColor: "var(--mantine-color-gray-0)" }}>
      <Table.Td colSpan={colSpan}>
        <Group gap={8} wrap="nowrap">
          <ThemeIcon variant="light" color={g.color || "grape"} radius="xl" size={24}>
            <AppIcon iconName={g.icon || "Users"} size={14} />
          </ThemeIcon>
          <Text size="md" fw={700}>
            {g.label}
          </Text>
          <Badge size="sm" variant="light" color="gray">
            {g.items.length}
          </Badge>
          <GroupFillBadge items={g.items} />
        </Group>
      </Table.Td>
    </Table.Tr>
  );

  // ── Desktop table ──
  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                {isSelectable && (
                  <Table.Th style={{ width: 44 }}>
                    <Checkbox checked={selection!.allSelected(selectableVisibleIds)} indeterminate={selection!.someSelected(selectableVisibleIds)} onChange={() => selection!.toggleAll(selectableVisibleIds)} disabled={selectableVisibleIds.length === 0} aria-label={t("common:selection.selectAll", { defaultValue: "Select all" })} />
                  </Table.Th>
                )}
                <Table.Th>{t("volunteers:slots.columns.name", { defaultValue: "Slot" })}</Table.Th>
                <Table.Th style={{ width: 110 }}>{t("volunteers:slots.columns.filled", { defaultValue: "Filled" })}</Table.Th>
                <Table.Th style={{ width: 240 }}>{t("volunteers:slots.columns.when", { defaultValue: "When" })}</Table.Th>
                <Table.Th style={{ width: 220 }}>{t("volunteers:slots.columns.where", { defaultValue: "Where" })}</Table.Th>
                <Table.Th style={{ width: 120 }}>{t("volunteers:slots.columns.reward", { defaultValue: "Reward" })}</Table.Th>
                <Table.Th style={{ width: 140 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 8 }).map((_, i) => <SlotTableRowSkeleton key={i} selectionColumn={isSelectable} />)
                : groups
                ? groups.flatMap((g) => [groupHeadingRow(g), ...g.items.map((slot) => <SlotTableRow key={slot.id} {...rowProps(slot)} />)])
                : slots.map((slot) => <SlotTableRow key={slot.id} {...rowProps(slot)} />)}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  // ── Tablet / mobile cards ──
  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 6 }).map((_, i) => (
          <Paper key={i} p="md" withBorder radius="md">
            <Group gap="md" wrap="nowrap" align="flex-start">
              <CardSkeletonCheckbox show={isSelectable} />
              <Skeleton circle height={40} width={40} />
              <CardSkeletonLines count={3} sizes={['md', 'sm', 'sm']} gap={1} />
              <CardSkeletonActions count={cardActions} />
            </Group>
          </Paper>
        ))}
      </Stack>
    );
  }

  const renderCard = (slot: VolunteerSlot) => (
    <SlotCard key={slot.id} {...rowProps(slot)} forceClose={shouldForceClose(slot.id || "")} onForceCloseComplete={() => handleForceCloseComplete(slot.id || "")} onSwipeStateChange={(isOpen) => handleSwipeStateChange(slot.id || "", isOpen)} />
  );

  return (
    <Stack gap="md">
      {groups ? (
        groups.map((g) => (
          <Stack key={g.key} gap="sm">
            <Group gap={8} wrap="nowrap" px={4}>
              <ThemeIcon variant="light" color={g.color || "grape"} radius="xl" size={24}>
                <AppIcon iconName={g.icon || "Users"} size={14} />
              </ThemeIcon>
              <Text size="md" fw={700}>
                {g.label}
              </Text>
              <Badge size="sm" variant="light" color="gray">
                {g.items.length}
              </Badge>
              <GroupFillBadge items={g.items} />
            </Group>
            {g.items.map(renderCard)}
          </Stack>
        ))
      ) : (
        <Stack gap="sm">{slots.map(renderCard)}</Stack>
      )}
      {loadMore}
    </Stack>
  );
};

export const VolunteerSlotsList = React.memo(VolunteerSlotsListComponent);
